import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  InquiryDetailSectionHeader,
  MaudCardFrame,
  ProviderAvatarBlock,
  SkeletonLoader,
} from '@equip.health/ui';
import { Section } from '../../../../common/Section';
import { useMemo } from 'react';
import {
  CareTeamProviderType,
  DEFAULT_CARE_TEAM_PROVIDER_TYPES,
} from '~/lib/constants/patients.constants';
import {
  getProviderType,
  getProviderTypeLabel,
} from '~/components/maud/providerAssignment/utils';

interface CareTeamProps {
  isLoading: boolean;
  onAssign: (providerType?: CareTeamProviderType) => void;
  providers: GetProviderByIdResponse[];
}

const MENTOR_ROLE_MAPPING: Record<string, string> = {
  [CareTeamProviderType.PEER_MENTOR]: CareTeamProviderType.RECOVERY_COACH,
  [CareTeamProviderType.RECOVERY_COACH]: CareTeamProviderType.PEER_MENTOR,
  [CareTeamProviderType.FAMILY_MENTOR]: CareTeamProviderType.CAREGIVER_COACH,
  [CareTeamProviderType.CAREGIVER_COACH]: CareTeamProviderType.FAMILY_MENTOR,
};

const normalizeMentorRole = (providerType: string): string => {
  if (
    DEFAULT_CARE_TEAM_PROVIDER_TYPES.includes(
      providerType as CareTeamProviderType,
    )
  ) {
    return providerType;
  }
  return MENTOR_ROLE_MAPPING[providerType] ?? providerType;
};

const CareTeam = ({ isLoading, onAssign, providers }: CareTeamProps) => {
  const TOPLEVEL_ID = 'inquiry-detail__care-team';

  const careTeamCards = useMemo(() => {
    const cards = [];

    const providerTypeMap: Record<string, GetProviderByIdResponse[]> =
      Object.fromEntries(DEFAULT_CARE_TEAM_PROVIDER_TYPES.map((t) => [t, []]));

    for (const provider of providers) {
      const providerType = normalizeMentorRole(getProviderType(provider));

      providerTypeMap[providerType]?.push(provider);
    }

    for (const providerType of DEFAULT_CARE_TEAM_PROVIDER_TYPES) {
      const matchingProviders = providerTypeMap[providerType];
      if (matchingProviders.length === 0) {
        cards.push(
          <MaudCardFrame
            id={`${TOPLEVEL_ID}__empty-${providerType}`}
            key={`${TOPLEVEL_ID}__empty-${providerType}`}
          >
            <ChakraFlex
              align="center"
              justify="space-between"
              sx={{
                '& [id*="avatar-block__avatar-image__avatar"]': {
                  backgroundColor: 'neutral.line',
                },
              }}
            >
              <ProviderAvatarBlock
                id={`${TOPLEVEL_ID}__empty-${providerType}`}
                legalName={{ firstName: '', lastName: '' }}
                providerTypeName={getProviderTypeLabel(providerType)}
                shouldShowProviderType
                variant="standard"
              />
              <FireflyButton
                id={`${TOPLEVEL_ID}__add-${providerType}`}
                leftIcon={FAIconName.plusRegular}
                onClick={() => onAssign(providerType)}
                variant="secondary"
              >
                Add
              </FireflyButton>
            </ChakraFlex>
          </MaudCardFrame>,
        );
        continue;
      }

      for (const provider of matchingProviders) {
        cards.push(
          <MaudCardFrame
            id={`${TOPLEVEL_ID}-${providerType}--${provider.userId}`}
            key={`${TOPLEVEL_ID}-${providerType}--${provider.userId}`}
          >
            <ChakraFlex align="center">
              <ProviderAvatarBlock
                avatarUrl={
                  provider.avatar?.thumbnailUrl || provider.avatar?.url
                }
                credentials={provider.credentials}
                displayName={{
                  firstName: provider.chosenName ?? provider.firstName,
                  lastName: provider.lastName,
                }}
                id={`${TOPLEVEL_ID}-${providerType}--${provider.userId}`}
                legalName={{
                  firstName: provider.firstName,
                  lastName: provider.lastName,
                }}
                pronouns={provider.pronouns}
                providerTypeName={getProviderTypeLabel(providerType)}
                shouldShowCredentials
                shouldShowPronouns
                shouldShowProviderType
                variant="standard"
              />
            </ChakraFlex>
          </MaudCardFrame>,
        );
      }
    }
    return cards;
  }, [providers, onAssign]);

  return (
    <Section id={TOPLEVEL_ID}>
      <InquiryDetailSectionHeader
        id={`${TOPLEVEL_ID}-header`}
        title="Provider team"
      />
      <SkeletonLoader id={TOPLEVEL_ID} isLoaded={!isLoading}>
        <Section id={`${TOPLEVEL_ID}-children`}>{careTeamCards}</Section>
      </SkeletonLoader>
    </Section>
  );
};

export default CareTeam;
